import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import AdvancedArrayInput from '../AdvancedArrayInput';
import CustomSelect from '../widgets/CustomSelect';
import AiAssistPanel from '../AiAssistPanel';
import { useAuth } from '../../hooks/useAuth';
import logo from '../../assets/logo192.png';
import '../../styles/TreatmentPlanModal.css';
import '../../styles/DischargeModal.css';

const TREATMENT_PLAN_MED_FIELDS = [
  { name: 'route', label: 'Route', type: 'dropdown', options: [
    { value: 'IV', label: 'IV' },
    { value: 'IM', label: 'IM' },
    { value: 'PO', label: 'PO' },
    { value: 'PR', label: 'PR' },
    { value: 'S/C', label: 'S/C' },
    { value: 'PV', label: 'PV' },
  ], width: '10%' },
  { name: 'Dosage', label: 'Dosage', type: 'text', width: '10%' },
  { name: 'frequency', label: 'Frequency', type: 'dropdown', options: [
    { value: 'OD', label: 'OD' },
    { value: 'BD', label: 'BD' },
    { value: 'TDS', label: 'TDS' },
    { value: 'QID', label: 'QID' },
    { value: 'One-Time', label: 'One-Time' },
    { value: 'PRN', label: 'PRN' },
    { value: 'Stat', label: 'Stat' },
  ], width: '8%' },
  { name: 'quantity', label: 'Qty', type: 'number', width: '8%' },
];

const resolveAssetUrl = (assetPath) => {
  if (!assetPath) return '';
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  const origin = window.location.origin;
  return `${origin}${assetPath.startsWith('/') ? '' : '/'}${assetPath}`;
};

export const DISPOSITION_OPTIONS = [
  { value: 'home', label: 'Home' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'ama', label: 'Left against advice' },
  { value: 'deceased', label: 'Deceased' },
];

export const CONDITION_OPTIONS = [
  { value: 'improved', label: 'Improved' },
  { value: 'stable', label: 'Stable' },
  { value: 'worsened', label: 'Worsened' },
  { value: 'deceased', label: 'Deceased' },
];

export const STATUS_LABELS = {
  admitted: 'Admitted',
  ready_for_discharge: 'Ready for Discharge',
  discharged: 'Discharged',
};

export const TAKE_HOME_STATUS_LABELS = {
  none: 'No discharge order',
  not_required: 'None prescribed',
  pending: 'Waiting for pharmacy',
  dispensed: 'Dispensed',
};

export const SHOW_DISCHARGE_PRINT = true;

const DISCHARGE_AI_PERMISSION = '/ai-assist/discharge-language-suggestions';

const DISCHARGE_AI_FIELD_MAPPING = {
  refined_diagnosis: 'discharge_diagnosis',
  refined_final_assessment: 'final_assessment',
  refined_follow_up: 'follow_up',
  refined_instructions: 'instructions',
};

const DISCHARGE_AI_SECTIONS = [
  { key: 'refined_diagnosis', title: 'Diagnosis', icon: '🩺' },
  { key: 'refined_final_assessment', title: 'Final assessment', icon: '📝' },
  { key: 'refined_follow_up', title: 'Follow-up', icon: '📅' },
  { key: 'refined_instructions', title: 'Instructions for the patient', icon: '📋' },
];

const normalizeForCompare = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const suggestionsDifferFromForm = (suggestions, form) => {
  if (!suggestions || !form) return false;
  return Object.entries(DISCHARGE_AI_FIELD_MAPPING).some(([aiKey, field]) => {
    const suggested = normalizeForCompare(suggestions[aiKey]);
    if (!suggested) return false;
    return suggested !== normalizeForCompare(form[field]);
  });
};

const hasMedicationRows = (items) => {
  if (!Array.isArray(items)) return false;
  return items.some((item) => {
    if (!item) return false;
    const name = item.name || item.item_name;
    return (typeof name === 'string' && name.trim().length > 0) || !!item.item_id;
  });
};

const takeHomeMedsKey = (items) => JSON.stringify(
  (Array.isArray(items) ? items : [])
    .filter((item) => item && (item.name || item.item_name || item.item_id))
    .map((item) => ({
      item_id: item.item_id || null,
      name: item.name || item.item_name || '',
      route: item.route || '',
      Dosage: item.Dosage || item.dosage || item.instructions || '',
      frequency: item.frequency || '',
      quantity: item.quantity === null || item.quantity === undefined || item.quantity === ''
        ? ''
        : String(item.quantity),
    }))
);

const formSnapshot = (data) => JSON.stringify({
  doctor_name: data?.doctor_name || '',
  disposition: data?.disposition || '',
  condition_at_discharge: data?.condition_at_discharge || '',
  discharge_diagnosis: data?.discharge_diagnosis || '',
  final_assessment: data?.final_assessment || '',
  follow_up: data?.follow_up || '',
  instructions: data?.instructions || '',
  take_home_medications: takeHomeMedsKey(data?.take_home_medications),
});

const isTakeHomeDispensed = (admission) => {
  const request = admission?.take_home_request;
  if (request) {
    return request.status === 'Dispensed' || !!request.dispensed;
  }
  return admission?.take_home_status === 'dispensed';
};

const CONFIRM_REPLACE_DISPENSED =
  'Take-home medications have already been dispensed. Saving will create a new pharmacy request for the updated list. Continue?';

export const printDischargeSheet = ({ admission, discharge }) => {
  if (!admission || !discharge) return;
  const patient = admission.Patient || {};
  const patientName = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim() || 'Patient';
  const meds = Array.isArray(discharge.take_home_medications) ? discharge.take_home_medications : [];
  const medRows = meds.length
    ? meds.map((med) => `
        <tr>
          <td>${med.name || med.item_name || ''}</td>
          <td>${med.route || ''}</td>
          <td>${med.Dosage || med.dosage || med.instructions || ''}</td>
          <td>${med.frequency || ''}</td>
          <td>${med.quantity || ''}</td>
        </tr>`).join('')
    : '<tr><td colspan="5">No take-home medications</td></tr>';

  const disposition = DISPOSITION_OPTIONS.find((option) => option.value === discharge.disposition)?.label || discharge.disposition;
  const condition = CONDITION_OPTIONS.find((option) => option.value === discharge.condition_at_discharge)?.label || discharge.condition_at_discharge;
  const logoSrc = resolveAssetUrl(logo);
  const html = `
    <html>
      <head>
        <title>Discharge Summary - ${patientName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
          .report-header { display: flex; align-items: center; border-bottom: 2px solid #333; padding-bottom: 8px; margin-bottom: 16px; }
          .lab-logo img { width: 150px; height: auto; margin-right: 16px; }
          .header-compact { flex: 1; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; }
          .lab-info h1 { font-size: 20px; margin: 0 0 4px; }
          .lab-info p, .patient-compact p, .report-meta p { margin: 2px 0; font-size: 13px; }
          h2 { font-size: 16px; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
          p, td, th { font-size: 13px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; }
          .meta { margin: 4px 0; }
        </style>
      </head>
      <body>
        <header class="report-header">
          <div class="lab-logo">
            <img src="${logoSrc}" alt="Shuman Hospital Logo" />
          </div>
          <div class="header-compact">
            <div class="lab-info">
              <h1>Shuman Hospital</h1>
              <p>15/17 Pultney Street, Cotton Tree</p>
              <p>Phone: (+232) 090 33 00 00</p>
            </div>
            <div class="patient-compact">
              <p><strong>ID:</strong> ${patient.patient_number || patient.patient_id || ''}</p>
              <p><strong>Name:</strong> ${patientName}</p>
              <p><strong>DOB:</strong> ${patient.patient_dob || ''}</p>
              <p><strong>Ward / Bed:</strong> ${admission.ward || ''} ${admission.bed ? `/ ${admission.bed}` : ''}</p>
            </div>
            <div class="report-meta">
              <p><strong>Admission:</strong> ${admission.admission_date ? new Date(admission.admission_date).toLocaleString() : ''}</p>
              <p><strong>Doctor:</strong> ${discharge.doctor_name || ''}</p>
              <p><strong>Signed:</strong> ${discharge.ordered_at ? new Date(discharge.ordered_at).toLocaleString() : ''}</p>
            </div>
          </div>
        </header>
        <h2>Discharge Summary</h2>
        <p class="meta"><strong>Diagnosis:</strong> ${discharge.discharge_diagnosis || ''}</p>
        <p class="meta"><strong>Condition:</strong> ${condition}</p>
        <p class="meta"><strong>Going:</strong> ${disposition}</p>
        <h2>Final Assessment</h2>
        <p>${(discharge.final_assessment || '').replace(/\n/g, '<br/>')}</p>
        ${discharge.follow_up ? `<h2>Follow-up</h2><p>${String(discharge.follow_up).replace(/\n/g, '<br/>')}</p>` : ''}
        ${discharge.instructions ? `<h2>Instructions for the patient</h2><p>${String(discharge.instructions).replace(/\n/g, '<br/>')}</p>` : ''}
        <h2>Take-home medications</h2>
        <table>
          <thead><tr><th>Medicine</th><th>Route</th><th>Dosage</th><th>Frequency</th><th>Qty</th></tr></thead>
          <tbody>${medRows}</tbody>
        </table>
      </body>
    </html>
  `;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Print discharge summary');
  iframe.style.position = 'fixed';
  iframe.style.left = '0';
  iframe.style.top = '0';
  iframe.style.width = '100vw';
  iframe.style.height = '100vh';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.zIndex = '-1';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;
  if (!frameWindow || !frameDocument) {
    iframe.remove();
    alert('Unable to print the discharge summary.');
    return;
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
  };

  const triggerPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch (error) {
      console.error('Error printing discharge summary:', error);
      alert('Unable to print the discharge summary.');
      cleanup();
    }
  };

  const images = Array.from(frameDocument.images || []);
  Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();
    return new Promise((resolve) => {
      image.onload = resolve;
      image.onerror = resolve;
    });
  })).then(() => {
    setTimeout(triggerPrint, 50);
  });

  if (typeof frameWindow.addEventListener === 'function') {
    frameWindow.addEventListener('afterprint', cleanup);
  }
  setTimeout(cleanup, 60000);
};

const emptyForm = (admissionId, diagnosis) => ({
  doctor_name: '',
  disposition: 'home',
  condition_at_discharge: 'improved',
  discharge_diagnosis: diagnosis || '',
  final_assessment: '',
  follow_up: '',
  instructions: '',
  take_home_medications: [],
  admission_id: admissionId,
});

const DischargeModal = ({
  admission,
  discharge,
  diagnosis,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { user, hasPermission } = useAuth();
  const [formData, setFormData] = useState(emptyForm(admission?.admission_id, diagnosis));
  const [submitting, setSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [aiPanelVisible, setAiPanelVisible] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiCached, setAiCached] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [wordingJustApplied, setWordingJustApplied] = useState(false);
  const skipAiOnNextSaveRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const isOrdered = discharge?.status === 'ordered';
  const isLocked = !!admission?.discharge_date;
  const takeHomeDispensed = isTakeHomeDispensed(admission);
  const canReviewLanguage = typeof hasPermission === 'function'
    && hasPermission(DISCHARGE_AI_PERMISSION, 'Execute');

  useEffect(() => {
    if (!isOpen) {
      setAiPanelVisible(false);
      setAiSuggestions(null);
      setAiError(null);
      setAiCached(false);
      setWordingJustApplied(false);
      skipAiOnNextSaveRef.current = false;
      return;
    }
    if (!admission) return;
    const nextForm = (isOrdered && discharge)
      ? {
        doctor_name: discharge.doctor_name || '',
        disposition: discharge.disposition || 'home',
        condition_at_discharge: discharge.condition_at_discharge || 'improved',
        discharge_diagnosis: discharge.discharge_diagnosis || diagnosis || '',
        final_assessment: discharge.final_assessment || '',
        follow_up: discharge.follow_up || '',
        instructions: discharge.instructions || '',
        take_home_medications: Array.isArray(discharge.take_home_medications) ? discharge.take_home_medications : [],
        admission_id: admission.admission_id,
      }
      : emptyForm(admission.admission_id, diagnosis);
    setFormData(nextForm);
    setSavedSnapshot(formSnapshot(nextForm));
    setCancelOpen(false);
    setCancelReason('');
  }, [isOpen, admission, discharge, diagnosis, isOrdered]);

  if (!isOpen) return null;

  const hasUnsavedChanges = savedSnapshot !== null && formSnapshot(formData) !== savedSnapshot;
  const closeIsDone = isOrdered && !hasUnsavedChanges;
  const showSaveButton = !isLocked && (!isOrdered || hasUnsavedChanges);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'disposition' && value === 'deceased') {
        next.condition_at_discharge = 'deceased';
      }
      return next;
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const requestLanguageReview = async (sourceForm) => {
    setAiPanelVisible(true);
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    setAiCached(false);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/ai/discharge-language-suggestions`,
        { admission_id: admission.admission_id }
      );
      if (response.data.success) {
        const data = response.data.data;
        if (!isOpenRef.current) return;
        setAiSuggestions(data);
        setAiCached(response.data.cached || false);
        if (!suggestionsDifferFromForm(data, sourceForm)) {
          setAiPanelVisible(false);
          onClose();
        }
      } else if (isOpenRef.current) {
        setAiError(response.data.error || 'Unexpected error');
      }
    } catch (err) {
      if (!isOpenRef.current) return;
      const msg = err.response?.data?.error || err.message || 'Failed to get language suggestions';
      setAiError(msg);
    } finally {
      if (isOpenRef.current) setAiLoading(false);
    }
  };

  const handleAiApplyAll = async (fieldUpdates) => {
    const nextForm = { ...formData, ...fieldUpdates };
    setFormData(nextForm);
    skipAiOnNextSaveRef.current = true;
    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/${admission.admission_id}/discharge`;
      const payload = {
        ...nextForm,
        updatedBy: user?.portal_id,
      };
      const response = await axios.put(url, payload);
      setSavedSnapshot(formSnapshot(nextForm));
      setWordingJustApplied(true);
      onSaved(response.data);
      setAiPanelVisible(false);
    } catch (error) {
      skipAiOnNextSaveRef.current = false;
      console.error('Error applying language suggestions:', error);
      alert(error.response?.data?.error || 'Failed to save suggested wording.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLocked) return;
    if (!formData.doctor_name) {
      alert('Doctor name is required.');
      return;
    }
    if (!formData.final_assessment.trim()) {
      alert('Final assessment is required.');
      return;
    }
    if (!hasMedicationRows(formData.take_home_medications)) {
      const proceedEmpty = window.confirm('No take-home medications? Click OK if the patient is going home without medicines.');
      if (!proceedEmpty) return;
    } else {
      const missingCatalog = formData.take_home_medications.filter((item) => (item.name || item.item_name) && !item.item_id);
      if (missingCatalog.length > 0) {
        alert('Take-home medications must be selected from the pharmacy list so they can be dispensed and billed.');
        return;
      }
      const incomplete = formData.take_home_medications.filter((item) =>
        (item.name || item.item_name) && (!item.route || !item.Dosage || !item.frequency)
      );
      if (incomplete.length > 0) {
        alert('For each take-home medication, Route, Dosage, and Frequency must be set.');
        return;
      }
      const missingQty = formData.take_home_medications.filter((item) => (item.name || item.item_name) && (!item.quantity || Number(item.quantity) <= 0));
      if (missingQty.length > 0) {
        alert('Please enter a quantity for each take-home medication so pharmacy knows how much to dispense.');
        return;
      }
    }
    const proceed = window.confirm(
      'This tells pharmacy and admin the patient is ready to leave. The bed is not freed until admin completes discharge.'
    );
    if (!proceed) return;

    const medsChanged = takeHomeMedsKey(formData.take_home_medications)
      !== takeHomeMedsKey(discharge?.take_home_medications);
    let replaceDispensedRequest = false;
    if (takeHomeDispensed && medsChanged) {
      if (!window.confirm(CONFIRM_REPLACE_DISPENSED)) return;
      replaceDispensedRequest = true;
    }

    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/${admission.admission_id}/discharge`;
      const payload = {
        ...formData,
        updatedBy: user?.portal_id,
        replace_dispensed_request: replaceDispensedRequest,
      };
      const saveDischarge = () => (isOrdered ? axios.put(url, payload) : axios.post(url, payload));
      let response;
      try {
        response = await saveDischarge();
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.code === 'TAKE_HOME_DISPENSED') {
          if (!window.confirm(error.response.data.error || CONFIRM_REPLACE_DISPENSED)) return;
          payload.replace_dispensed_request = true;
          response = await saveDischarge();
        } else {
          throw error;
        }
      }
      onSaved(response.data);
      setSavedSnapshot(formSnapshot(formData));
      if (skipAiOnNextSaveRef.current) {
        skipAiOnNextSaveRef.current = false;
        return;
      }
      if (!canReviewLanguage) {
        onClose();
        return;
      }
      await requestLanguageReview(formData);
    } catch (error) {
      console.error('Error saving discharge:', error);
      alert(error.response?.data?.error || 'Failed to save discharge.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert('Please enter a reason for cancelling the discharge order.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/${admission.admission_id}/discharge/cancel`,
        { reason: cancelReason, updatedBy: user?.portal_id }
      );
      onSaved(response.data);
      onClose();
    } catch (error) {
      console.error('Error cancelling discharge:', error);
      alert(error.response?.data?.error || 'Failed to cancel discharge.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="modal-overlay">
      <div className="treatment-modal-content discharge-modal-content">
        <h2>{isOrdered ? 'Update Discharge' : 'Discharge Patient'}</h2>
        <p className="discharge-help">
          Record the final assessment and take-home medicines. Admin will enter the discharge date when the patient actually leaves.
        </p>
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {takeHomeDispensed && !isLocked && (
            <div className="discharge-form-warning" role="alert">
              Take-home medication has already been dispensed. If you change the list and continue, a new pharmacy request will be created. The original dispensed request is not changed.
            </div>
          )}
          <div className="form-group">
            <label>Doctor Name*</label>
            <CustomSelect
              name="doctor_name"
              value={formData.doctor_name}
              endpoint="/hospital-portal/api/employees/doctors"
              onChange={handleSelectChange}
              required
              valueField="doctor_name"
              labelField="doctor_name"
              placeholder="Select a doctor"
              searchable
              error={formData.doctor_name ? '' : 'Doctor name is required'}
            />
          </div>

          <div className="form-group">
            <label>Discharge diagnosis</label>
            <textarea
              name="discharge_diagnosis"
              value={formData.discharge_diagnosis}
              onChange={handleChange}
              rows={2}
            />
          </div>

          <div className="form-group-inline">
            <div className="form-group">
              <label>Condition*</label>
              <select
                name="condition_at_discharge"
                value={formData.condition_at_discharge}
                onChange={handleChange}
                required
              >
                {CONDITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Going to*</label>
              <select
                name="disposition"
                value={formData.disposition}
                onChange={handleChange}
                required
              >
                {DISPOSITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Final assessment*</label>
            <textarea
              name="final_assessment"
              value={formData.final_assessment}
              onChange={handleChange}
              rows={5}
              required
              placeholder="Current status and brief hospital course"
            />
          </div>

          <div className="form-group">
            <label>Follow-up</label>
            <textarea
              name="follow_up"
              value={formData.follow_up}
              onChange={handleChange}
              rows={2}
              placeholder="Clinic, date, or return if..."
            />
          </div>

          <div className="form-group">
            <label>Instructions for the patient</label>
            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleChange}
              rows={3}
              placeholder="Activity, diet, warning signs"
            />
          </div>

          <div className="form-group">
            <label>Take-home medications</label>
            <AdvancedArrayInput
              name="take_home_medications"
              value={formData.take_home_medications}
              endpoint="/hospital-portal/api/items/search/by-name"
              onChange={(name, items) => setFormData((prev) => ({ ...prev, [name]: items }))}
              additionalFields={TREATMENT_PLAN_MED_FIELDS}
              showItemNotFoundWarning={false}
              disabled={isLocked}
            />
          </div>

          {cancelOpen && (
            <div className="form-group">
              <label>Reason for cancelling*</label>
              <textarea
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                rows={2}
                placeholder="Why is this discharge order being cancelled?"
              />
            </div>
          )}

          {wordingJustApplied && !hasUnsavedChanges && (
            <div className="discharge-form-success" role="status">
              Wording saved. Print the sheet or tap Done.
            </div>
          )}
          <div className="form-actions">
            <button
              type="button"
              className={`discharge-action-btn ${closeIsDone ? 'discharge-action-btn--primary' : 'discharge-action-btn--secondary'}`}
              onClick={onClose}
              disabled={submitting}
            >
              {closeIsDone ? 'Done' : 'Close'}
            </button>
            {isOrdered && !isLocked && (
              cancelOpen ? (
                <button
                  type="button"
                  className="discharge-action-btn discharge-action-btn--danger"
                  onClick={handleCancelOrder}
                  disabled={submitting}
                >
                  Confirm cancel
                </button>
              ) : (
                <button
                  type="button"
                  className="discharge-action-btn discharge-action-btn--secondary"
                  onClick={() => setCancelOpen(true)}
                  disabled={submitting}
                >
                  Cancel discharge order
                </button>
              )
            )}
            {SHOW_DISCHARGE_PRINT && isOrdered && (
              <button
                type="button"
                className="discharge-action-btn discharge-action-btn--print"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => printDischargeSheet({ admission, discharge: { ...discharge, ...formData } })}
              >
                Print
              </button>
            )}
            {showSaveButton && (
              <button
                type="submit"
                className="discharge-action-btn discharge-action-btn--primary"
                disabled={submitting || aiLoading}
              >
                {submitting ? 'Saving...' : (isOrdered ? 'Update Discharge' : 'Sign Discharge')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
    {createPortal(
      <div className="discharge-ai-portal">
        <AiAssistPanel
          visible={aiPanelVisible}
          onClose={() => setAiPanelVisible(false)}
          onApplyAll={handleAiApplyAll}
          suggestions={aiSuggestions}
          loading={aiLoading}
          error={aiError}
          cached={aiCached}
          fieldMapping={DISCHARGE_AI_FIELD_MAPPING}
          onRetry={() => requestLanguageReview(formData)}
          mode="suggestions"
          sections={DISCHARGE_AI_SECTIONS}
          panelTitle="Discharge wording"
        />
      </div>,
      document.body
    )}
    </>
  );
};

export default DischargeModal;
